import { createOfferInput, type OfferDetail } from '@fos/shared/contracts/offers';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Segmented } from '../../../ui/Segmented';
import { toast } from '../../../ui/Toast';
import { ClientPicker } from '../../clients/components/ClientPicker';
import { issuesToErrors, parseAmountInput, parseHoursInput, zodIssues } from '../../maintenance/form-utils';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { offerHref } from '../offer-format';

const FIELD_KEYS = [
  'clientId',
  'title',
  'content.scope',
  'content.estimatedSeconds',
  'content.package',
  'content.rate',
  'content.timeline',
] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

type Mode = 'estimate' | 'package';

interface FormState {
  clientId: string | null;
  title: string;
  scope: string;
  mode: Mode;
  hours: string;
  packageText: string;
  rate: string;
  timeline: string;
}

const empty = (clientId: string | null): FormState => ({
  clientId,
  title: '',
  scope: '',
  mode: 'estimate',
  hours: '',
  packageText: '',
  rate: '',
  timeline: '',
});

/**
 * „Nowa oferta” (modal 560 px; ekran czeka na projekt w Figmie — wzorzec modali „Nowy projekt” / „Dodaj zasób”):
 * klient, tytuł, zakres, szacowane godziny albo pakiet godzin, stawka (tylko z „Widzi stawki i kwoty”; puste =
 * stawka klienta), termin orientacyjny. Pozostałe sekcje (rezultat, wyłączenia, założenia, warunki) uzupełnia się
 * na stronie oferty — po zapisie otwiera się jej edycja.
 */
export function NewOfferDialog({
  open,
  onOpenChange,
  defaultClientId = null,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultClientId?: string | null;
  /** zamiast przejścia do oferty (np. w testach) */
  onCreated?: (offer: OfferDetail) => void;
}) {
  const access = useAccess();
  const seesRates = access.flag('seesRates');
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const create = trpc.offers.create.useMutation();
  const [form, setForm] = useState<FormState>(() => empty(defaultClientId));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset tylko przy otwarciu
  useEffect(() => {
    if (!open) return;
    setForm(empty(defaultClientId));
    setErrors({});
    setFormError(null);
  }, [open]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const next: Partial<Record<FieldKey, string>> = {};
    if (!form.clientId) next.clientId = 'Wybierz klienta';
    const hours = parseHoursInput(form.hours);
    if (hours === undefined) next['content.estimatedSeconds'] = 'Podaj liczbę godzin, np. 40 albo 12,5';
    let rate: string | null | undefined;
    if (seesRates) {
      rate = parseAmountInput(form.rate);
      if (rate === undefined) next['content.rate'] = 'Podaj stawkę, np. 150 albo 150,50';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    const input = {
      clientId: form.clientId as string,
      title: form.title,
      content: {
        scope: form.scope,
        estimatedSeconds: hours ?? null,
        package: form.mode === 'package' ? form.packageText.trim() || 'Pakiet godzin' : null,
        timeline: form.timeline.trim() || null,
        ...(seesRates && rate ? { rate } : {}),
      },
    };
    const parsed = createOfferInput.safeParse(input);
    if (!parsed.success) {
      setErrors(issuesToErrors(zodIssues(parsed.error), FIELD_KEYS));
      return;
    }
    try {
      const offer = await create.mutateAsync(input);
      toast.success(`Utworzono ofertę #${offer.number}`);
      await utils.offers.invalidate();
      onOpenChange(false);
      if (onCreated) onCreated(offer);
      else void navigate(`${offerHref(offer.id)}?edytuj=1`);
    } catch (err) {
      if (appErrorCode(err) === 'VALIDATION_ERROR') {
        const fieldErrors = issuesToErrors(validationIssues(err), FIELD_KEYS);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }
      setFormError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Nowa oferta"
      className={FULLSCREEN_ON_PHONE}
      description="Pozostałe sekcje uzupełnisz na stronie oferty."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="new-offer-form" loading={create.isPending}>
            Utwórz ofertę
          </Button>
        </>
      }
    >
      <form
        id="new-offer-form"
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="flex flex-col gap-4"
        aria-label="Nowa oferta"
      >
        <Field label="Klient" error={errors.clientId}>
          <ClientPicker value={form.clientId} onChange={(v) => set('clientId', v)} excludePartial aria-label="Klient" />
        </Field>
        <Field label="Tytuł" error={errors.title}>
          <Input
            autoFocus
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="np. Redesign strony"
          />
        </Field>
        <Field label="Zakres" error={errors['content.scope']} hint="Co zrobimy. Możesz użyć Markdown, np. listy „- ”.">
          <Textarea rows={4} value={form.scope} onChange={(e) => set('scope', e.target.value)} />
        </Field>
        <div className="flex flex-col gap-2">
          <Segmented
            aria-label="Rozliczenie"
            value={form.mode}
            onValueChange={(v) => set('mode', v)}
            options={[
              { value: 'estimate', label: 'Szacowane godziny' },
              { value: 'package', label: 'Pakiet godzin' },
            ]}
            className="self-start"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={form.mode === 'package' ? 'Godzin w pakiecie' : 'Szacowany czas (h)'}
              error={errors['content.estimatedSeconds']}
            >
              <Input
                mono
                inputMode="decimal"
                value={form.hours}
                onChange={(e) => set('hours', e.target.value)}
                placeholder="np. 40"
              />
            </Field>
            {seesRates && (
              <Field label="Stawka netto (zł/h)" error={errors['content.rate']} hint="Puste = stawka klienta">
                <Input
                  mono
                  inputMode="decimal"
                  value={form.rate}
                  onChange={(e) => set('rate', e.target.value)}
                  placeholder="np. 150"
                />
              </Field>
            )}
          </div>
          {form.mode === 'package' && (
            <Field label="Pakiet" error={errors['content.package']}>
              <Input
                value={form.packageText}
                onChange={(e) => set('packageText', e.target.value)}
                placeholder="np. miesięcznie, niewykorzystane godziny przechodzą na kolejny miesiąc"
              />
            </Field>
          )}
        </div>
        <Field label="Termin orientacyjny" error={errors['content.timeline']}>
          <Input
            value={form.timeline}
            onChange={(e) => set('timeline', e.target.value)}
            placeholder="np. ok. 6 tygodni od akceptacji"
          />
        </Field>
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
